/**
 * What a player is told when their room stops existing.
 *
 * Pure and separate from the screen that shows it, so every reason the server
 * can send has words that a test can check. A closed room is the end of the
 * page: there is nothing left to retry, only a way back to the start.
 */

import type { RoomClosedReason } from '@overcooked/shared'

export interface ClosedMessage {
  title: string
  detail: string
}

const MESSAGES: Record<RoomClosedReason, ClosedMessage> = {
  // The server cannot tell a code that never existed from one whose room has
  // since closed — both are simply not there — so this says both, rather than
  // telling somebody with a typo that their game has finished.
  'not-found': {
    title: 'No open game with this code',
    detail: 'The code may be mistyped, or the game may have ended. Check the code or start a new game.',
  },
  'nobody-joined': {
    title: 'Nobody joined in time',
    detail: 'You can start a new game.',
  },
  abandoned: {
    title: 'This game was closed',
    detail: 'It sat empty for too long. You can start a new game.',
  },
  idle: {
    title: 'This game was closed',
    detail: 'Nobody started another round for a while. You can start a new game.',
  },
}

/** Words for every reason, and a sensible fallback for one a newer server invents. */
export function closedMessage(reason: RoomClosedReason): ClosedMessage {
  return MESSAGES[reason] ?? MESSAGES['not-found']
}

/** What a third visitor is told, before any gate is drawn. */
export function fullMessage(roomCode: string): ClosedMessage {
  return {
    title: `Game ${roomCode} is full`,
    detail: 'Both chefs are already here. If one was you, open the link in the tab you were playing in.',
  }
}

/** What a `/r/…` link that is not a game code at all is told. */
export function malformedCodeMessage(text: string): ClosedMessage {
  return {
    title: "That isn't a game code",
    detail: text
      ? `"${text}" isn't four letters or numbers. Check the link you were sent.`
      : 'Game codes are four letters or numbers. Check the link you were sent.',
  }
}
